/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */
import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { style, animate, transition, state, trigger } from '@angular/animations';

import { Router, ActivatedRoute } from '@angular/router';

import { BsModalService } from 'ngx-bootstrap/modal';

import { CacheService } from '../../global/ng2-cache/services/cache.service';
import { SessionService } from '../../services/session2.service';
import { GlobalMessageService } from '../../services/global-message.service';
import { PartnerService } from '../../services/partner.service';

import { Constants, Partner, EmailType } from '../../global/constants';
import { AutomateTask, AutomateJob, AutomateFilter, PartnerList, PartnerField, PartnerStaticFieldMap, PartnerSiftrockFieldMap } from '../../global/models';
import { AlertDialogComponent } from '../../global/alert-dialog/alert-dialog.component';
import { ConfirmDialogComponent } from '../../global/confirm-dialog/confirm-dialog.component';
import { WizardStep } from '../../global/wizard-steps/wizard-steps.component';
import { AutomateJobService } from '../../services/automate-job.service';
import { PartnerMapOptions } from '../../global/partner-map/partner-map.component';

@Component({
  selector: 'app-route-track-detail',
  templateUrl: './route-track-detail.component.html',
  animations: [
    trigger('draftState',
      [
        state('inactive', style({
          opacity: 0,
          transform: 'scale(.9)'
        })),
        state('active', style({
          opacity: 1,
          transform: 'scale(1)'
        })),
        transition('inactive => active', animate('300ms ease-in'))
      ]
    )
  ]
})
export class RouteTrackDetailComponent implements OnInit {
  loadingPage = true;
  session: any;
  id: string = '';
  automateJob: AutomateJob;

  // these are needed in dozens of places both in UI and controller, keep a reference for convenience
  routeTask: AutomateTask;
  trackTask: AutomateTask;

  // state management
  //showAdvanced: boolean = false;
  showRouteStatic: boolean = false; // need this due to complexity of controls
  routeHoverState: any = {
    static: false,
    dynamic: false,
    leadowner: false,
    track: false
  };

  taskIssues: any = {
    route: '',
    track: ''
  };

  // GLOBAL CONTROLS

  // Wizard steps
  wizardSteps: Array<WizardStep> = [
    { stepNumber: 1, label: 'emails' },
    { stepNumber: 2, label: 'routing', },
    { stepNumber: 3, label: 'tracking', },
    { stepNumber: 4, label: 'finish', }
  ];
  selectedStep: WizardStep = this.wizardSteps[0];

  //email-filter-group options
  currentFilters: AutomateFilter[];
  emailFilterGroupOptions = {
    emailTypes: {
      required: true,
      default: {
        include: ['human', 'humandirect'],
        exclude: []
      },
      standard: ['human', 'humandirect', 'unsubscribe', 'spamshield'],
      advanced: ['bounce', 'changed', 'general', 'left', 'system', 'unknown', 'vacation']
    },
    language: {
      badgeShowLimit: 5,
      required: true,
      default: {
        include: [],
        exclude: []
      }
    },
    source: {
      badgeShowLimit: 0,
      required: false,
      default: {
        include: [],
        exclude: []
      },
      allowAdd: true,
      minimumCount: 0,
      concatLength: 512
    },
    supportedFilters: [
      this.constants.automateFilterTypes.subject.name,
      this.constants.automateFilterTypes.body.name,
      this.constants.automateFilterTypes.emailType.name,
      this.constants.automateFilterTypes.language.name,
      this.constants.automateFilterTypes.source.name
    ]
  }

  // Route controls
  staticRoutes: string[] = [];
  newStatic: string = '';
  addingStatic: boolean = false;
  dynamicDomainSet: boolean = false;
  dynamicDomain: string = '';
  selectedPartnerLeadOwner: Partner;
  selectedFieldLeadOwner: PartnerField;
  leadOwnerFallbackAddresses: Array<string> = [];

  // Partner map control
  partnerMapOptions: PartnerMapOptions = {
    listSelector: true,
    staticFieldSelector: true,
    siftrockFieldSelector: true,
    senderFieldSelector: false,
    skillType: 'legacy'
  };
  selectedPartner: Partner;
  selectedList: PartnerList;
  staticFieldMap: Array<PartnerStaticFieldMap> = [];
  siftrockFieldMap: Array<PartnerSiftrockFieldMap> = [];

  // for draft saved/status
  draftTimer: any = null;
  draftSaved: string = '';
  draftState: string = 'inactive';

  constructor(private route: ActivatedRoute,
    private router: Router,
    private cacheService: CacheService,
    private sessionService: SessionService,
    private modalService: BsModalService,
    private constants: Constants,
    private globalMessageService: GlobalMessageService,
    private partnerService: PartnerService,
    private automateJobService: AutomateJobService) { }

  ngOnInit() {
    this.session = this.cacheService.get('session');
    if (!this.session) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.session = session;
          this.initView();
        }
      )
    }
    else {
      this.initView();
    }
  }

  /**
   * Change wizard step
   * @param step
   */
  changeStep(step: WizardStep) {
    if (step.label == 'finish') {
      // this.router.navigate(['/config/routetrack']);
      window.location.href =  '/automation/skills'
    }
    else {
      this.selectedStep = step;
    }
  }

  /**
   * Change the name of the automate_job
   */
  changeName() {
    this.stage();
  }

  changeFilter(filters: AutomateFilter[]) {
    this.automateJob.filters = filters;
    this.stage();
  }

  /**
   * Change the route task routing type (static, dynamic, lead owner lookup)
   * @param type
   */
  changeRouteType(type: string) {
    if (type == this.routeTask.settings.type) {
      // short-circuit on clicking the same type
      return;
    }

    this.verifyChangeRouteType(this.routeTask, this.routeTask.settings.type, type).then(
      canChange => {
        if (canChange) {
          this.routeTask.settings.type = type;
          // on any change we will remove dynamic and lead owner settings
          this.routeTask.settings.dynamic = '';
          this.routeTask.settings.lead_owner = {};
          this.dynamicDomain = '';
          this.dynamicDomainSet = false;
          this.selectedPartnerLeadOwner = null;
          this.selectedFieldLeadOwner = null;
          this.leadOwnerFallbackAddresses = [];
          this.stage(this.routeTask);
        }
      }
    );
  }

  /**
   * Add an email address to the static list of email addresses for routing
   */
  addStaticRoute() {
    if (this.routeTask.settings.static.findIndex(s => { return s.email_address == this.newStatic }) >= 0) {
      // duplicate - don't allow
      this.modalService.show(AlertDialogComponent, Object.assign({}, {
        initialState: {
          message: `${this.newStatic} is already on your list of specific people.`,
          confirmBtn: 'Ok'
        }
      }));

      this.newStatic = '';
      this.addingStatic = false;
      return;
    }

    // not a duplicate - add to list
    this.routeTask.settings.static.push({ email_address: this.newStatic, created_at: new Date() });

    // keep them sorted
    this.routeTask.settings.static.sort((a: any, b: any) => {
      if (a.email_address < b.email_address) {
        return -1;
      }
      else {
        return 1
      }
    });
    this.staticRoutes = this.routeTask.settings.static;
    this.newStatic = '';
    this.addingStatic = false;
    this.stage(this.routeTask);
  }

  removeStaticRoute(emailAddress: string) {
    this.routeTask.settings.static.splice(this.routeTask.settings.static.findIndex(s => { return s.email_address == emailAddress }), 1);
    this.staticRoutes = this.routeTask.settings.static;
    this.stage(this.routeTask);
  }

  setDynamicRoute() {
    this.routeTask.settings.dynamic = this.dynamicDomain;
    this.dynamicDomainSet = true;
    this.stage(this.routeTask);
    this.globalMessageService.message('Dynamic route saved');
  }

  removeDynamicRoute() {
    this.dynamicDomain = '';
    this.routeTask.settings.dynamic = this.dynamicDomain;
    this.dynamicDomainSet = false;
    this.stage(this.routeTask);
  }

  /**
   * Event from partner-leadowner-picker when the Partner dropdown changes
   * @param partner
   */
  changeLeadOwnerPartner(partner: Partner) {
    this.routeTask.settings.lead_owner.partner = partner.name;
    this.stage(this.routeTask);
  }

  /**
   * Event from partner-leadowner-picker when the Field dropdown changes
   * @param field
   */
  changeLeadOwnerField(field: PartnerField) {
    this.routeTask.settings.lead_owner.field = field;
    this.stage(this.routeTask);
  }

  /**
   * Event from partner-leadowner-picker when the fallback addresses change
   * @param addresses
   */
  changeLeadOwnerFallback(addresses: Array<string>) {
    this.routeTask.settings.lead_owner.fallback = addresses;
    this.stage(this.routeTask);
  }

  /**
   * Event bubbled up from partner-picker through partner-map
   * The value will be null if the partner was cleared
   * @param partner
   */
  changePartner(partner: Partner) {
    this.selectedPartner = partner;
    if (partner) {
      this.trackTask.settings.partner = partner.name;
    }
    else {
      this.trackTask.settings.partner = '';
    }
    this.stage(this.trackTask);
  }

  changeList(list: PartnerList) {
    this.selectedList = list;
    if (list) {
      this.trackTask.settings.list = list;
    }
    else {
      this.trackTask.settings.list = {};
    }
    this.stage(this.trackTask);
  }

  changeStaticField(fields: Array<PartnerStaticFieldMap>) {
    this.trackTask.settings.static_fields = [];
    if (fields.length > 0) {
      fields.forEach(f => {
        this.trackTask.settings.static_fields.push({
          id: f.partnerField.id,
          display: f.partnerField.display,
          name: f.partnerField.name,
          type: f.partnerField.type,
          value: f.value
        });
      });
    }
    this.stage(this.trackTask);
  }

  changeSiftrockField(fields: Array<PartnerSiftrockFieldMap>) {
    this.trackTask.settings.siftrock_fields = [];
    if (fields.length > 0) {
      fields.forEach(f => {
        this.trackTask.settings.siftrock_fields.push({
          id: f.partnerField.id,
          display: f.partnerField.display,
          name: f.partnerField.name,
          type: f.partnerField.type,
          value: f.siftrockField.fieldName
        });
      });
    }
    this.stage(this.trackTask);
  }

  private initView() {
    this.route.params.subscribe(
      params => {
        this.id = params['id'];
        if (this.id == 'new') {
          this.initNew();
          this.refreshTasks();
          this.setupFilters();
          this.loadingPage = false;
        }
        else {
          Observable.forkJoin(
            this.automateJobService.getJob(this.id)
            //this.partnerService.getPartners()
          ).subscribe(
            (result: any) => {
              this.automateJob = result[0];
              this.refreshTasks();
              this.setupFilters();
              this.setupRoute();
              this.setupTrack();
              this.loadingPage = false;
            },
            error => {
              // TODO
            }
          )
        }
      }
    )
  }

  private setupFilters() {
    this.currentFilters = this.automateJob.filters;
  }

  private setupRoute() {
    this.staticRoutes = this.routeTask.settings.static;
    this.dynamicDomain = this.routeTask.settings.dynamic;
    if (this.dynamicDomain.length > 0) {
      this.dynamicDomainSet = true;
    }

    // lead_owner configuration
    if (this.routeTask.settings.lead_owner.partner) {
      this.selectedPartnerLeadOwner = this.constants.partners.find(p => { return p.name == this.routeTask.settings.lead_owner.partner });
      if (this.routeTask.settings.lead_owner.field) {
        this.selectedFieldLeadOwner = this.routeTask.settings.lead_owner.field;
      }
      if (this.routeTask.settings.lead_owner.fallback) {
        this.leadOwnerFallbackAddresses = this.routeTask.settings.lead_owner.fallback;
      }
    }

    // some state management happens here
    this.checkValidRoute();
  }

  private setupTrack() {
    if (this.trackTask.settings.partner.length > 0) {
      this.selectedPartner = this.constants.partners.find(p => { return p.name == this.trackTask.settings.partner });
    }

    if (this.trackTask.settings.list && this.trackTask.settings.list.id) {
      this.selectedList = {
        id: this.trackTask.settings.list.id,
        name: this.trackTask.settings.list.name
      };
    }

    if (this.trackTask.settings.static_fields.length > 0) {
      this.trackTask.settings.static_fields.forEach(f => {
        this.staticFieldMap.push({
          partnerField: {
            id: f.id,
            name: f.name,
            display: f.display,
            type: f.type
          },
          value: f.value
        });
      });
    }

    if (this.trackTask.settings.siftrock_fields.length > 0) {
      this.trackTask.settings.siftrock_fields.forEach(f => {
        let srField = this.constants.siftrockFields.find(sf => { return sf.fieldName == f.value });
        this.siftrockFieldMap.push({
          partnerField: {
            id: f.id,
            name: f.name,
            display: f.display,
            type: f.type
          },
          siftrockField: srField
        });
      });
    }

    // some state management happens here
    this.checkValidTrack();
  }

  /**
   * Stage changes - on a 3 second timer to avoid constant saving when job name is being edited
   *
   * @param taskIndex If a task was changed, let's update the updated_at value
   */
  private stage(task: AutomateTask = null) {
    this.checkValidTrack();
    this.checkValidRoute();

    if (task) {
      task.updated_at = new Date();
    }

    if (this.routeTask.invalid_reason || this.trackTask.invalid_reason) {
      this.automateJob.enabled = false;
    }

    if (this.draftTimer) {
      clearTimeout(this.draftTimer);
    }
    this.draftTimer = setTimeout(() => {
      this.save();
    }, 1500);
  }

  private save() {
    this.draftState = 'inactive';

    this.automateJobService.saveJob(this.automateJob).subscribe(
      (result: any) => {
        this.automateJob = result;
        this.draftSaved = new Date().toLocaleTimeString();
        this.draftState = 'active';
        this.refreshTasks();
      },
      error => {
        this.globalMessageService.error();
      }
    )
  }

  /**
   * Set local copies of tasks for convenience
   */
  private refreshTasks() {
    this.routeTask = this.automateJob.tasks[0];
    this.trackTask = this.automateJob.tasks[1];
  }

  /**
   * Check validity of route task and set meta data appropriately
   */
  private checkValidRoute() {
    let valid: boolean = true;
    let invalidReason: string = null;
    this.taskIssues.route = null;
    if (this.routeTask) {
      if (!this.routeTask.settings.type || this.routeTask.settings.type.length == 0) {
        valid = false;
      }
      else {
        if (this.routeTask.settings.type == 'static' && this.routeTask.settings.static.length == 0) {
          valid = false;
          invalidReason = 'At least one email address is required for routing';
        }
        else if (this.routeTask.settings.type == 'dynamic' && this.routeTask.settings.dynamic.length == 0) {
          valid = false;
          invalidReason = 'A domain is required for dynamic routing';
        }
        else if (this.routeTask.settings.type == 'leadowner') {
          if (!this.routeTask.settings.lead_owner.partner || this.routeTask.settings.lead_owner.partner.length == 0) {
            valid = false;
            invalidReason = 'An integration must be selected for lead owner lookup routing';
          }
          else if (!this.routeTask.settings.lead_owner.field || !this.routeTask.settings.lead_owner.field.id) {
            valid = false;
            invalidReason = 'Lead owner lookup routing requires a field';
          }
          else if (!this.routeTask.settings.lead_owner.fallback || this.routeTask.settings.lead_owner.fallback.length == 0) {
            valid = false;
            invalidReason = 'At least one fallback address is required for lead owner lookup routing';
          }
        }
        else if (this.routeTask.settings.type == 'track') {
          // "track only" types are invalid so the back-end knows not to create job_q items
          valid = false;
        }

        // set static route state
        this.showRouteStatic = false;
        if (this.routeTask.settings.type == 'static' || this.routeTask.settings.type == 'dynamic' || this.routeTask.settings.type == 'leadowner') {
          this.showRouteStatic = true;
        }
        if (this.routeTask.settings.type == 'leadowner' && !this.session.f.leadowner) {
          this.showRouteStatic = false;
        }
      }
    }

    this.routeTask.valid = valid;
    this.routeTask.invalid_reason = invalidReason;
  }

  /**
   * Check validity of track task and set meta data appropriately
   */
  private checkValidTrack() {
    let valid: boolean = true;
    let invalidReason: string = null;
    this.taskIssues.track = '';
    if (this.trackTask) {
      if (this.trackTask.settings.partner.length == 0) {
        valid = false;
      }
      else {
        // partner picked, check for minimum of list add or field update
        if (!this.trackTask.settings.list.id && this.trackTask.settings.static_fields.length == 0 && this.trackTask.settings.siftrock_fields.length == 0) {
          valid = false;
        }
      }
    }

    this.trackTask.valid = valid;
    this.trackTask.invalid_reason = invalidReason;
  }

  private initNew() {
    this.automateJob = {
      _id: 'new',
      name: 'New Route & Track Rule',
      account_id: this.cacheService.get('session').account_id,
      type: 'routetrack',
      enabled: false,
      deleted: false,
      track_changes: false,
      filters: [],
      tasks: [this.automateJobService.initTask('route'), this.automateJobService.initTask('track')]
    };

    this.automateJob.filters.push({ type: 'email_type', include: [], exclude: [] });
    this.automateJob.filters.push({ type: 'source', include: [], exclude: [] });
    this.automateJob.filters.push({ type: 'language', include: ['all'], exclude: [] });
    this.automateJob.filters.push({ type: 'body', include: [], exclude: [] });
    this.automateJob.filters.push({ type: 'subject', include: [], exclude: [] });

    this.automateJob.tasks[0].settings = {
      partner: 'siftrock',
      type: null,
      static: [],
      dynamic: '',
      lead_owner: {},
    };

    this.automateJob.tasks[1].settings = {
      partner: '',
      list: {},
      static_fields: [],
      siftrock_fields: []
    };

    this.automateJob.filters[this.getFilterIndexByType('email_type')].include.push('human');
    this.automateJob.filters[this.getFilterIndexByType('email_type')].include.push('humandirect');
  }

  private verifyChangeRouteType(routeTask: AutomateTask, oldType: string, newType: string): Promise<boolean> {
    let needDialog: boolean = false;
    let dialogMessage: string = '';

    if (oldType == 'dynamic' && routeTask.settings.dynamic.length > 0) {
      needDialog = true;
      dialogMessage = 'WARNING: you have a dynamic route set. If you change the route type we will clear the dynamic route configuration. Please confirm you\'d like to do this.';
    }

    if (oldType == 'leadowner' && routeTask.settings.lead_owner.partner) {
      needDialog = true;
      dialogMessage = 'WARNING: you have lead owner lookup routing set. If you change the route type we will clear the lead owner lookup configuration. Please confirm you\'d like to do this.';
    }

    if (needDialog) {
      return new Promise((resolve, reject) => {
        let me = this;
        this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
          initialState: {
            message: dialogMessage,
            yesBtn: 'Confirm',
            confirm: function() {
              resolve(true);
            }
          }
        }));
      });
    }
    else {
      return Promise.resolve(true);
    }
  }

  getFilterIndexByType(type: string) {
    return this.automateJob.filters.findIndex(t => { return t.type == type });
  }
}



// WEBPACK FOOTER //
// ./client/app/config/route-track/route-track-detail.component.ts